import "core-js/modules/es.array.push.js";
import { useAppApiClient } from 'app/api/app-api/AppApiClientContext';
import { useApiErrorToast } from 'app/api/app-api/apiHooksHelpers';
import { useUpdateOrganizationMutation } from 'app/modules/organizations/api/organizationsApiHooks';
import { Field, Form, Formik } from 'formik';
import { UserRole, useLegolandApiClient, useMeDetailsQuery, useMeQuery } from 'legoland-sdk/dist/experimental';
import moment from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Alert, Button, Checkbox, FormGroup, Input, Modal, ModalActions, ModalContent, ModalHeader, ModalLayout, Text, Tooltip, tombac } from 'tombac';
import { ContractType } from "../contractTypes";
import { useAddContractMutation, useResolveContractOverlaps, useUpdateContractMutation } from "../contractsApiHooks";
import { Table, Tbody, Td, Th, Thead, Tr } from "../list/Table";
import { ContractAccessTypeField } from "./fields/ContractAccessTypeField";
import { ContractDateRangeFields } from "./fields/ContractDateRangeFields";
import { ContractOrganizationFields } from "./fields/ContractOrganizationFields";
import { ContractProductsField } from "./fields/ContractProductsField";
import { ContractTypeField } from "./fields/ContractTypeField";
import { useContractValidationSchema } from "./useContractValidationSchema";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const newContract = {
  organization: {
    id: null,
    sapId: ''
  }
};
export const ContractFormModal = ({
  contract = newContract,
  intent = 'default',
  onCancel,
  onSaveSuccess,
  ...modalProps
}) => {
  const addApiErrorToast = useApiErrorToast();
  const isNewContract = contract.id === undefined;
  const hasUsers = contract.users?.length > 0;

  // Current user
  const meDetailsQuery = useMeDetailsQuery();
  const meRole = meDetailsQuery?.data?.meDetails?.role;

  // Overlap errors
  const [overlapErrors, setOverlapErrors] = useState([]);
  const overlapAlertRef = useRef(null);
  const {
    mutateAsync: resolveOverlaps
  } = useResolveContractOverlaps();

  // Form
  const validationSchema = useContractValidationSchema();
  const initialValues = useMemo(() => contractToValues(contract), [contract]);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  useEffect(() => {
    // Reset selected organization when the modal is opened
    setSelectedOrganization(null);
  }, [modalProps.isOpen]);
  const [isCloneUsersChecked, setIsCloneUsersChecked] = useState(true);
  const {
    mutateAsync: addContract
  } = useAddContractMutation();
  const {
    mutateAsync: updateContract
  } = useUpdateContractMutation();
  const {
    mutateAsync: updateOrganization
  } = useUpdateOrganizationMutation();
  const copyContractLimits = useCopyContractLimits();
  const copyContractUsers = useCopyContractUsers();
  async function handleSubmit(values) {
    const contractToSave = prepareToSave(values);
    let addedContract;
    if (!isNewContract && intent !== 'clone') {
      try {
        const overlaps = await resolveOverlaps({
          contract: {
            id: contract.id,
            startDate: contractToSave.startDate,
            endDate: contractToSave.endDate,
            type: contractToSave.type,
            products: contractToSave.products,
            users: contract.users.map(u => ({
              email: u.email
            }))
          }
        });
        setOverlapErrors(overlaps);
        if (overlaps && overlaps.length > 0) {
          overlapAlertRef.current?.scrollIntoView({
            behavior: 'smooth'
          });
          return;
        }
      } catch (e) {
        console.error(e);
      }
    }
    try {
      let cloneOverlaps = [];
      if (contractToSave.organization?.sapId && !selectedOrganization?.sapId) {
        await updateOrganization({
          changes: {
            sapId: contractToSave.organization.sapId
          },
          organizationId: contractToSave.organization.id
        });
      }
      if (isNewContract || intent === 'clone') {
        addedContract = await addContract({
          contract: contractToSave
        });
        if (intent === 'clone') {
          await copyContractLimits(contract, addedContract);
          if (isCloneUsersChecked) {
            cloneOverlaps = await copyContractUsers(contract.id, addedContract.id);
          }
        }
      } else {
        await updateContract({
          contractId: contract.id,
          changes: contractToSave
        });
      }
      if (onSaveSuccess) {
        const info = intent === 'clone' ? {
          userOverlaps: cloneOverlaps
        } : {};
        onSaveSuccess(addedContract, info);
      }
    } catch (error) {
      addApiErrorToast(error, 'Failed to save the contract');
    }
  }
  return /*#__PURE__*/_jsx(Modal, {
    ...modalProps,
    "data-sentry-element": "Modal",
    "data-sentry-component": "ContractFormModal",
    "data-sentry-source-file": "ContractFormModal.tsx",
    children: /*#__PURE__*/_jsx(Formik, {
      initialValues: initialValues,
      onSubmit: handleSubmit,
      validationSchema: validationSchema,
      "data-sentry-element": "Formik",
      "data-sentry-source-file": "ContractFormModal.tsx",
      "data-sentry-element": "Formik",
      "data-sentry-element": "Formik",
      "data-sentry-element": "Formik",
      children: ({
        errors,
        isSubmitting,
        touched,
        values
      }) => {
        const areas = [];
        areas.push(['products', 'products'], ['contract-id', 'contract-name'], ['date-range', 'date-range'], ['contract-type', 'contract-type'], ['organization', 'sap-id']);
        if (values.type) {
          if (values.type === ContractType.License && meRole != UserRole.Distributor) {
            areas.push(['access-type', 'access-type']);
          }
        }
        areas.push(['contract-notes', 'contract-notes']);
        if (overlapErrors.length > 0) {
          areas.push(['alert', 'alert']);
        }
        const isAreaVisible = area => areas.find(row => row.includes(area));
        return /*#__PURE__*/_jsxs(ModalLayout, {
          $width: "auto",
          as: Form,
          children: [/*#__PURE__*/_jsxs(ModalHeader, {
            $m: "3sp 5sp 0",
            fontSize: 20,
            fontWeight: 700,
            children: [intent === 'clone' ? 'Clone' : isNewContract ? 'Create new' : 'Edit', ' ', "contract"]
          }), /*#__PURE__*/_jsx(ModalContent, {
            $borderBottom: intent === 'clone' ? '0' : undefined,
            $p: "1sp 5sp 5sp",
            variant: "scrollable",
            children: /*#__PURE__*/_jsxs(FieldGrid, {
              areas: areas.map(row => row.join(' ')),
              children: [/*#__PURE__*/_jsx(ContractProductsField, {
                $gridArea: "products",
                $mb: "2sp",
                contract: contract,
                name: "products",
                userRole: meRole
              }), /*#__PURE__*/_jsx(FormGroup, {
                $gridArea: "contract-id",
                error: touched.contractId && errors.contractId,
                htmlFor: "field-contract-id",
                label: "Contract ID",
                children: /*#__PURE__*/_jsx(Field, {
                  as: Input,
                  id: "field-contract-id",
                  invalid: Boolean(touched.contractId && errors.contractId),
                  name: "contractId"
                })
              }), /*#__PURE__*/_jsx(FormGroup, {
                $gridArea: "contract-name",
                error: touched.name && errors.name,
                htmlFor: "field-contract-name",
                label: "Name / Legal number",
                children: /*#__PURE__*/_jsx(Field, {
                  as: Input,
                  id: "field-contract-name",
                  invalid: Boolean(touched.name && errors.name),
                  name: "name",
                  placeholder: "Unnamed contract"
                })
              }), /*#__PURE__*/_jsx(ContractTypeField, {
                $gridArea: "contract-type",
                contract: contract,
                intent: intent,
                name: "type",
                userRole: meRole
              }), /*#__PURE__*/_jsx(ContractDateRangeFields, {
                $gridArea: "date-range",
                contract: contract
              }), isAreaVisible('organization') && /*#__PURE__*/_jsx(ContractOrganizationFields, {
                contract: contract,
                intent: intent,
                isCloneUsersChecked: isCloneUsersChecked,
                onOrganizationSelect: o => setSelectedOrganization(o),
                selectedOrganization: selectedOrganization,
                userRole: meRole
              }), isAreaVisible('access-type') && meRole !== UserRole.Distributor && /*#__PURE__*/_jsx(ContractAccessTypeField, {
                $gridArea: "access-type",
                name: "accessType"
              }), /*#__PURE__*/_jsx(FormGroup, {
                $gridArea: "contract-notes",
                htmlFor: "field-contract-notes",
                label: "Notes",
                children: /*#__PURE__*/_jsx(Field, {
                  as: Input,
                  id: "field-contract-notes",
                  name: "notes"
                })
              }), isAreaVisible('alert') && /*#__PURE__*/_jsx("div", {
                ref: overlapAlertRef,
                style: {
                  gridArea: 'alert'
                },
                children: /*#__PURE__*/_jsxs(Alert, {
                  title: "Cannot edit contract",
                  variant: "danger",
                  children: [/*#__PURE__*/_jsxs("span", {
                    children: ["Unable to edit due to conflicting contracts.", ' ']
                  }), /*#__PURE__*/_jsx(Tooltip, {
                    content: /*#__PURE__*/_jsxs(_Fragment, {
                      children: [/*#__PURE__*/_jsx(Text, {
                        $mb: "1sp",
                        children: "Conflicted users:"
                      }), /*#__PURE__*/_jsxs(Table, {
                        children: [/*#__PURE__*/_jsx(Thead, {
                          className: "UsersList__tableHead",
                          children: /*#__PURE__*/_jsxs(Tr, {
                            children: [/*#__PURE__*/_jsx(Th, {
                              children: "User"
                            }), /*#__PURE__*/_jsx(Th, {
                              children: "Contract"
                            })]
                          })
                        }), /*#__PURE__*/_jsx(Tbody, {
                          children: overlapErrors.map(el => /*#__PURE__*/_jsxs(Tr, {
                            children: [/*#__PURE__*/_jsx(Td, {
                              children: el.user.email
                            }), /*#__PURE__*/_jsx(Td, {
                              children: /*#__PURE__*/_jsx(Link, {
                                target: "_blank",
                                to: `/contracts/${el.contract.id}`,
                                children: el.contract.name
                              })
                            })]
                          }, `${el.contract.id}${el.user.email}`))
                        })]
                      })]
                    }),
                    children: ({
                      ref,
                      toggle
                    }) => /*#__PURE__*/_jsx("span", {
                      ref: ref,
                      onClick: toggle,
                      children: /*#__PURE__*/_jsx(Text, {
                        $display: "inline",
                        variant: "interactive",
                        children: "View details"
                      })
                    })
                  })]
                })
              })]
            })
          }), intent === 'clone' && /*#__PURE__*/_jsx(Alert, {
            title: "Any limits set in the original contract will also apply to the new one.",
            variant: "info",
            children: /*#__PURE__*/_jsx(Text, {
              $color: "--neutral-700",
              fontSize: 12,
              children: "After cloning, you can manage users, limits, and other contract details."
            })
          }), /*#__PURE__*/_jsxs(ModalActions, {
            $m: "3sp 5sp",
            secondary: intent === 'clone' && hasUsers && /*#__PURE__*/_jsx(Checkbox, {
              $alignItems: "center",
              $display: "flex",
              checked: isCloneUsersChecked,
              label: "Clone with assigned users",
              labelPlacement: "before",
              onChange: e => setIsCloneUsersChecked(e.target.checked),
              variant: "toggle"
            }),
            children: [/*#__PURE__*/_jsx(Button, {
              $ml: "auto",
              onClick: onCancel,
              type: "button",
              variant: "flat",
              children: "Cancel"
            }), /*#__PURE__*/_jsx(Button, {
              busy: isSubmitting,
              type: "submit",
              variant: "primary",
              children: intent === 'clone' ? 'Clone' : isNewContract ? 'Create' : 'Save'
            })]
          })]
        });
      }
    })
  });
};
function contractToValues({
  accessType = null,
  contractId,
  endDate,
  name,
  notes,
  organization: {
    id: organizationId,
    sapId
  },
  products,
  startDate,
  type = null
}) {
  return {
    accessType,
    contractId: contractId ?? '',
    name: name ?? '',
    organization: {
      id: organizationId,
      sapId: sapId ?? ''
    },
    products: products?.map(p => p.id) ?? [],
    type,
    startDate: startDate ?? moment(new Date()).format('YYYY-MM-DD'),
    endDate: endDate ?? undefined,
    notes: notes ?? ''
  };
}
const FieldGrid = styled.div.withConfig({
  displayName: "ContractFormModal__FieldGrid"
})(["align-items:flex-start;display:grid;gap:", ";grid-template-areas:", ";grid-template-columns:", ";"], tombac.space(3, 6), ({
  areas
}) => areas.map(a => `'${a}'`).join(' '), tombac.unit(300, 300));
function filterEmptyValues(values) {
  const entries = Object.entries(values).map(([name, value]) => [name, value === '' ? undefined : value]);
  return Object.fromEntries(entries);
}
function prepareToSave(values) {
  const newValues = filterEmptyValues(values);
  if (newValues.type !== ContractType.License) {
    delete newValues.accessType;
  }
  const contract = {
    ...newValues,
    products: newValues.products.map(id => ({
      id
    }))
  };
  return contract;
}
function useCopyContractLimits() {
  const {
    getLimitSchemas
  } = useLegolandApiClient();
  const {
    getContractLimits,
    setContractProductLimits,
    setContractProductSharedLimits
  } = useAppApiClient();
  const meQuery = useMeQuery();
  const currentUser = meQuery.data?.me;
  return async function copyContractLimits(sourceContract, targetContract) {
    const schemaResponse = await getLimitSchemas({});
    if (!schemaResponse.ok) throw schemaResponse;
    const schemas = await schemaResponse.json();
    const limitsResponse = await getContractLimits({
      contractId: sourceContract.id
    });
    if (!limitsResponse.ok) throw limitsResponse;
    const limits = await limitsResponse.json();
    for (const {
      overrides,
      product
    } of limits.products) {
      if (Object.keys(overrides).length === 0) continue;
      if (!targetContract.products?.find(p => p.id === product.id)) continue;
      const filteredOverrides = Object.fromEntries(Object.entries(overrides).filter(([name]) => schemas.find(schema => schema.name === name && schema.customizable && (currentUser?.role === UserRole.Admin || currentUser?.role === schema.requiredRole))));
      const setLimitsResponse = await setContractProductLimits({
        contractId: targetContract.id,
        productId: product.id,
        limits: {
          overrides: filteredOverrides
        }
      });
      if (!setLimitsResponse.ok) throw setLimitsResponse;
    }
    if (Object.keys(limits.package.limits).length === 0) return;
    const setSharedLimitsResponse = await setContractProductSharedLimits({
      contractId: targetContract.id,
      limits: {
        limits: limits.package.limits
      }
    });
    if (!setSharedLimitsResponse.ok) throw setSharedLimitsResponse;
  };
}
function useCopyContractUsers() {
  const {
    getContract,
    getContractUsers,
    resolveContractOverlaps,
    setContractUsers
  } = useAppApiClient();
  return async function copyContractUsers(sourceContractId, targetContractId) {
    // Get users from source contract

    const usersResponse = await getContractUsers({
      contractId: sourceContractId
    });
    if (!usersResponse.ok) throw usersResponse;
    const users = await usersResponse.json();

    // Get target contract

    const contractResponse = await getContract({
      contractId: targetContractId
    });
    if (!contractResponse.ok) throw contractResponse;
    const contract = await contractResponse.json();

    // Check for overlaps

    const overlapsResponse = await resolveContractOverlaps({
      contract: {
        ...contract,
        users: users.map(u => ({
          email: u.email
        }))
      }
    });
    if (!overlapsResponse.ok) throw overlapsResponse;
    const overlaps = await overlapsResponse.json();

    // Set users

    const validUsers = users.filter(u => !overlaps.find(o => o.user.email === u.email));
    const setUsersResponse = await setContractUsers({
      contractId: targetContractId,
      users: validUsers
    });
    if (!setUsersResponse.ok) throw setUsersResponse;
    return overlaps;
  };
}